import { getUTCNow, isAfter, Unpacked } from '@epicuro-next/utilities';
import { InventoryItem } from 'graphql-api/gql-api-interfaces';

import { ProductSizeType } from '../../models';
import { getNowDateContainer } from '../../shared/now-date-container/get-now-date-container';
import { isTrueDemandInventory } from '../../shared/true-demand-inventory';

export type InventoriesByMonth = {
  [dateString: string]: Unpacked<
    NonNullable<ProductSizeType>['inventoryItems']
  >;
};

export type InventoryOverviewItem = {
  deliveryPeriod: string;
  availableTo: string;
  maxQuantity: number;
  inventoryId: string;
  inventoryExternalId: string;
};

// just a helper type to describe innerly created data for its own consumption
// notice that it's almost identical to InventoryOverviewItem
type RawAvailability = {
  availableFrom: string;
  availableTo: string | null;
  maxQuantity: number;
  inventoryId: string;
  inventoryExternalId: string;
};

export const availableInventoriesForSelectedSizeForOrderType = (
  inventories: Partial<InventoryItem>[],
  splitInventoriesForSameDate = false,
): InventoriesByMonth => {
  return inventories.reduce((acc, inventory) => {
    const availableFromDateUtc = inventory.availableFromDateUtc as string;
    const dateKey =
      splitInventoriesForSameDate ||
      isTrueDemandInventory(inventory.inventoryId as string)
        ? `${availableFromDateUtc}-${inventory.inventoryExternalId}`
        : availableFromDateUtc;

    const quantity: number =
      (inventory.quantity ?? Number.POSITIVE_INFINITY) +
      (acc[dateKey]?.quantity || 0);

    return {
      ...acc,
      [dateKey]: acc[dateKey]
        ? {
            ...acc[dateKey],
            quantity,
          }
        : { ...inventory, quantity },
    } as InventoriesByMonth;
  }, {} as InventoriesByMonth);
};

export const availableInventoriesQuantitiesForDeliveryPeriod = (
  availableInventories: InventoriesByMonth,
  splitInventoriesForSameDate = false,
): InventoryOverviewItem[] => {
  const now = getUTCNow();
  // STEP 1 - convert available inventories into raw availability objects
  const availabilityArray: RawAvailability[] = Object.keys(
    availableInventories,
  ).map((dateString) => {
    return {
      availableFrom:
        availableInventories[dateString]?.availableFromDateUtc ?? dateString,
      availableTo: availableInventories[dateString]?.availableToDateUtc ?? null,
      maxQuantity: availableInventories[dateString]?.quantity ?? 0,
      inventoryId: availableInventories[dateString]?.inventoryId as string,
      inventoryExternalId: availableInventories[dateString]
        ?.inventoryExternalId as string,
    };
  });

  const pastAvailabilities = availabilityArray.filter(
    (date) =>
      now > date.availableFrom && !isTrueDemandInventory(date.inventoryId),
  );

  const futureAvailabilities = availabilityArray.filter(
    (date) => now < date.availableFrom,
  );

  const trueDemandAvailabilities = availabilityArray
    .filter((i) => isTrueDemandInventory(i.inventoryId))
    .map((i) => rawAvailabilityToInventoryOverview(i, now));

  // STEP 2 - map each future raw availability into separate overview object
  const futureInventoriesOverview: InventoryOverviewItem[] = [
    ...futureAvailabilities.map((i) =>
      rawAvailabilityToInventoryOverview(i, now, i.availableFrom),
    ),
  ];

  // STEP 3 - if "past" availabilities are present, we prepend array with bulk and normal availabilites
  if (pastAvailabilities.length > 0) {
    const nowInventories = getNowInventories(
      pastAvailabilities,
      now,
      splitInventoriesForSameDate,
    ).filter(
      // we begin with dummy empty inventory, but we might have no real inventories to merge into that
      // if so, we should not provide this inventory to the array
      ({ maxQuantity }) => maxQuantity > 0,
    );

    if (nowInventories.length > 0) {
      return [
        ...nowInventories,
        ...futureInventoriesOverview,
        ...trueDemandAvailabilities,
      ];
    }
  }

  return futureInventoriesOverview.concat(trueDemandAvailabilities);
};

function getNowInventories(
  pastAvailabilities: RawAvailability[],
  now: string,
  splitInventoriesForSameDate = false,
): InventoryOverviewItem[] {
  if (splitInventoriesForSameDate) {
    return pastAvailabilities.map((i) =>
      rawAvailabilityToInventoryOverview(i, now),
    );
  }

  return [
    pastAvailabilities.reduce(
      (acc, current) => {
        return {
          ...acc,
          availableTo: isAfter(
            new Date(acc.availableTo),
            new Date(current.availableTo || now),
          )
            ? acc.availableTo
            : current.availableTo || now,
          maxQuantity:
            acc.maxQuantity + (current.maxQuantity ?? Number.POSITIVE_INFINITY),
          inventoryId: current.inventoryId,
          inventoryExternalId: current.inventoryExternalId,
        };
      },
      {
        deliveryPeriod: getNowDateContainer(),
        availableTo: now,
        maxQuantity: 0,
        inventoryId: '',
        inventoryExternalId: '',
      },
    ),
  ];
}

function rawAvailabilityToInventoryOverview(
  {
    maxQuantity,
    inventoryId,
    inventoryExternalId,
    availableTo,
  }: RawAvailability,
  now: string,
  deliveryPeriod: string = getNowDateContainer(),
): InventoryOverviewItem {
  return {
    deliveryPeriod,
    availableTo: availableTo || now,
    maxQuantity,
    inventoryId,
    inventoryExternalId,
  };
}
